import { lastValueFrom, Subject } from 'rxjs'
import { map, takeUntil } from 'rxjs/operators'
import { inject, Injectable } from '@angular/core'
import { HttpClient, HttpEvent, HttpEventType, HttpHeaders } from '@angular/common/http'

import { Asset, AssetMini } from 'elements-sdk'
import { APIService } from 'api'
import { LoggerFactory } from 'common/log'
import { PanelService } from 'panel/services/panel.service'

interface PanelWindow extends Window {
  nodeRequire?: any
}

declare let window: PanelWindow

class Deferred<T> {
  promise: Promise<T>
  reject: (reason?: unknown) => void
  resolve: (value: T) => void

  constructor () {
    this.promise = new Promise((resolve, reject) => {
      this.reject = reject
      this.resolve = resolve
    })
  }
}

class Job {
  aborted = false
  abortSignal = new Subject<void>()

  constructor (private syncId: string) { }

  abort () {
    this.aborted = true
    this.abortSignal.next()
  }
}

@Injectable({ providedIn: 'root' })
export class ProxyCacheService {
  private readonly logger = inject(LoggerFactory).create('ProxyCacheService')
  private maxJobs = 2
  private chunkSize = 1024 * 1024 * 1

  cacheQueue: [string, number | undefined][] = []
  runningJobs: Job[] = []
  private node: any
  cachePath: string

  // TODO: Revise/Replace Promise handling
  private assetDeferreds = new Map<string, Deferred<void>>()

  constructor (
    private panel: PanelService,
    private api: APIService,
    private http: HttpClient,
  ) {
    if (panel.native && panel.plugin.hasProxy) {
      this.node = {
        fs: window.nodeRequire('fs'),
        path: window.nodeRequire('path'),
      }
      this.cachePath = this.node.path.join(
        panel.plugin.getSystemPath(
          panel.plugin.systemPath.MY_DOCUMENTS!),
        'ELEMENTS Proxy Cache',
      )

      this.node.Buffer = window.nodeRequire('buffer').Buffer
      this.ensureCachePath()
    }
  }

  async startCaching (): Promise<void> {
    while (this.runningJobs.length < this.maxJobs) {
      const queued = this.cacheQueue.shift()
      if (queued === undefined) {
        break
      }

      const id = queued[0]
      const proxyId = queued[1]
      try {
        await this.cacheAsset(id, proxyId)

        this.startCaching()
        const deferred = this.assetDeferreds.get(id)

        if (deferred) {
          deferred.resolve()
          this.assetDeferreds.delete(id)
        }
      } catch {
        this.startCaching()
        const deferred = this.assetDeferreds.get(id)

        if (deferred) {
          deferred.reject()
          this.assetDeferreds.delete(id)
        }
      }
    }
  }

  abortAll (): void {
    this.cacheQueue = []
    for (const job of this.runningJobs) {
      job.abort()
    }
  }

  isCached (asset: Asset | AssetMini, proxyId?: number): Promise<boolean> {
    return new Promise(resolve => {
      this.node.fs.stat(
        this.getProxyPath(asset.syncId, proxyId),
        (err, stats) => {
          const proxy = asset.proxies?.find(item => item.id === proxyId)
          const generated = proxy?.generatedDate ?? new Date(0)
          resolve(!err && stats.mtime >= generated)
        },
      )
    })
  }

  getProxyPath (syncId: string, proxyId?: number): string {
    this.ensureCachePath()
    let proxyIdSuffix = ''
    if (proxyId !== undefined) {
      proxyIdSuffix = `_${proxyId}`
    }
    return this.node.path.join(this.cachePath, `${syncId}${proxyIdSuffix}.mp4`)
  }

  async scheduleCacheAsset (asset: Asset | AssetMini, proxyId?: number): Promise<void> {
    if (await this.isCached(asset, proxyId)) {
      this.logger.info('Already cached', asset.syncId)
      return
    }

    if (!this.assetDeferreds.get(asset.syncId)) {
      const deferred = new Deferred<void>()
      this.assetDeferreds.set(asset.syncId, deferred)
    }

    if (!this.cacheQueue.includes([asset.syncId, proxyId])) {
      this.cacheQueue.push([asset.syncId, proxyId])
    }

    this.startCaching()
    await this.assetDeferreds.get(asset.syncId)?.promise
  }

  ensureCachePath (): void {
    if (!this.cachePath) {
      this.cachePath = this.node.path.join(
        this.panel.plugin.getSystemPath(
          this.panel.plugin.systemPath.MY_DOCUMENTS!),
        'ELEMENTS Proxy Cache',
      )
    }

    this.node.fs.stat(this.cachePath, err => {
      if (err) {
        if (this.panel.app === 'PPRO') {
          this.node.fs.mkdir(this.cachePath, { recursive: true }, mkdirErr => {
            if (mkdirErr) {
              this.logger.error('Could not create cache path', this.cachePath, mkdirErr)
            }
          })
        } else {
          this.node.fs.mkdirSync(this.cachePath, { recursive: true })
        }
      }
    })
  }

  async cacheAsset (syncId: string, proxyId?: number): Promise<void> {
    const job = new Job(syncId)
    this.runningJobs.push(job)
    this.ensureCachePath()

    const proxyPath = this.getProxyPath(syncId, proxyId)
    let cached = false

    try {
      const assets = await this.api.mediaLibrary.getAllAssets({
        syncId,
        includeProxies: true,
      })
      this.logger.info('Caching asset', syncId)
      if (!assets.length) {
        throw Error('Asset for syncId not found')
      }

      let proxyURL = (assets[0]!.proxies ?? [])
        .find(item => item.id === proxyId)?.urls.proxy

      if (!proxyURL) {
        throw Error('Missing defaultProxy URL')
      }

      const date = new Date().getTime()
      proxyURL += `?date=${date}`

      const stream = this.node.fs.createWriteStream(proxyPath)

      let headers = new HttpHeaders({
        Range: `bytes=0-0`,
      })
      const response = await lastValueFrom(
        this.http.get(proxyURL, {
          observe: 'response',
          responseType: 'arraybuffer',
          headers,
        }))

      let bytesDownloaded = 0
      let rangeSize = response.headers.get('Content-Range')?.split('/')[1]
      if (rangeSize === '*') {
        rangeSize = undefined
      }

      const contentLength = parseInt(rangeSize ?? '0')
      const promise = new Promise((resolve, reject) => {
        stream.on('finish', resolve)
        stream.on('error', reject)
      })

      while (bytesDownloaded < contentLength && !job.aborted && stream.writable) {
        const requestOffset = bytesDownloaded
        const requestLimit = Math.min(contentLength - 1, requestOffset + this.chunkSize - 1)

        headers = new HttpHeaders({
          Range: `bytes=${requestOffset}-${requestLimit}`,
        })
        const observable = this.http.get(proxyURL, {
          reportProgress: true,
          observe: 'events',
          responseType: 'arraybuffer',
          headers,
        })

        const chunkResponse: HttpEvent<ArrayBuffer> = await lastValueFrom(
          observable.pipe(
            takeUntil(job.abortSignal),
            map((event) => {
              if (event.type === HttpEventType.DownloadProgress) {
                const downloaded = requestOffset + event.loaded
                this.logger.debug(syncId, 'progress', downloaded, '/', contentLength)
              }
              return event
            }),
          ),
        )

        if (chunkResponse.type === HttpEventType.Response) {
          if (!chunkResponse.body) {
            continue
          }

          const buffer = new this.node.Buffer(new Uint8Array(chunkResponse.body), 'binary')
          bytesDownloaded += buffer.length
          stream.write(buffer, (error) => {
            if (error) {
              job.abort()
              stream.destroy(error)
            }
          })
        }
      }

      if (stream.writable) {
        stream.end()
      }

      if (!job.aborted) {
        await promise
      }

      if (bytesDownloaded >= contentLength) {
        this.logger.info('Cached asset', syncId)
        cached = true
      }
    } catch (error) {
      this.logger.error('Could not cache', syncId, error)
      throw error
    } finally {
      if (job.aborted || !cached) {
        this.logger.info('Caching aborted', syncId)
        this.node.fs.unlinkSync(proxyPath)
      }
      this.runningJobs = this.runningJobs.filter(x => x !== job)
    }
  }
}
